import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { Save, Loader2, Bot, Upload, Trash2, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const AI_MODELS = [
  { value: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash (rápido, económico)' },
  { value: 'google/gemini-2.5-pro', label: 'Gemini 2.5 Pro (alta calidad)' },
  { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite (ultra rápido)' },
  { value: 'openai/gpt-5-mini', label: 'GPT-5 Mini (equilibrado)' },
  { value: 'openai/gpt-5', label: 'GPT-5 (máxima calidad)' },
];

interface LiaConfig {
  id: string;
  system_prompt: string;
  model: string;
  temperature: number;
  max_tokens: number;
  min_rrf_score?: number;
}


interface RagDocument {
  id: string;
  title: string;
  content: string;
  file_url: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export default function LiaConfigTab() {
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Config query
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['lia_config'],
    queryFn: async () => {
      const { data, error } = await supabase.from('lia_config' as any).select('*').limit(1).single();
      if (error) return { id: '', system_prompt: 'Eres LIA, asistente de LÍDER IA.', model: 'google/gemini-2.5-flash', temperature: 0.7, max_tokens: 2000, min_rrf_score: 0 } as LiaConfig;
      return data as unknown as LiaConfig;
    },
  });

  // RAG documents query
  const { data: ragDocs = [], isLoading: ragLoading } = useQuery({
    queryKey: ['lia_rag_documents'],
    queryFn: async () => {
      const { data, error } = await supabase.from('lia_rag_documents' as any).select('*').order('sort_order');
      if (error) return [];
      return data as unknown as RagDocument[];
    },
  });

  // Config form state
  const DEFAULT_PROMPT = 'Eres LIA, asistente de LÍDER IA.';
  const [configForm, setConfigForm] = useState<Partial<LiaConfig> | null>(null);
  const rawConfig = { ...{ system_prompt: DEFAULT_PROMPT, model: 'google/gemini-2.5-flash', temperature: 0.7, max_tokens: 2000, min_rrf_score: 0 }, ...config, ...configForm };
  const activeConfig = { ...rawConfig, system_prompt: rawConfig.system_prompt?.trim() ? rawConfig.system_prompt : DEFAULT_PROMPT };
  const [uploading, setUploading] = useState(false);

  const updateConfig = useMutation({
    mutationFn: async (updates: Partial<LiaConfig>) => {
      if (config?.id) {
        const { error } = await supabase.from('lia_config' as any).update(updates).eq('id', config.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('lia_config' as any).insert(updates);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lia_config'] });
      setConfigForm(null);
      toast({ title: 'Configuración guardada' });
    },
    onError: () => toast({ title: 'Error al guardar', variant: 'destructive' }),
  });

  const deleteDocMutation = useMutation({
    mutationFn: async (doc: RagDocument) => {
      // Delete from storage if file_url exists
      if (doc.file_url) {
        const path = doc.file_url.split('/lia-rag/')[1];
        if (path) {
          await supabase.storage.from('lia-rag').remove([path]);
        }
      }
      // Delete from DB (chunks cascade via FK)
      const { error } = await supabase.from('lia_rag_documents' as any).delete().eq('id', doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lia_rag_documents'] });
      toast({ title: 'Documento eliminado' });
    },
    onError: () => toast({ title: 'Error al eliminar', variant: 'destructive' }),
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExts = ['.pdf', '.txt', '.md'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedExts.includes(ext)) {
      toast({ title: 'Solo se permiten archivos PDF, TXT o MD', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      // Upload to storage
      const fileName = `${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage.from('lia-rag').upload(fileName, file);
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('lia-rag').getPublicUrl(fileName);

      // Insert document record
      const { data: docRecord, error: insertError } = await supabase
        .from('lia_rag_documents' as any)
        .insert({ title: file.name, content: '', file_url: urlData.publicUrl, is_active: true, sort_order: 0 })
        .select()
        .single();
      if (insertError) throw insertError;

      const isPdf = ext === '.pdf';
      let textContent = '';
      if (!isPdf) {
        textContent = await file.text();
      }

      await supabase.functions.invoke('process-lia-document', {
        body: {
          document_id: (docRecord as any).id,
          ...(isPdf ? { file_url: urlData.publicUrl } : { text_content: textContent }),
        },
      });

      qc.invalidateQueries({ queryKey: ['lia_rag_documents'] });
      toast({ title: 'Documento cargado y procesado' });
    } catch (err: any) {
      console.error('Upload error:', err);
      toast({ title: 'Error al subir el documento', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (configLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Config Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bot className="w-5 h-5 text-primary" />
            Configuración del Agente LIA
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>System Prompt</Label>
            <Textarea
              className="min-h-[150px] font-mono text-xs mt-1"
              value={activeConfig.system_prompt}
              onChange={(e) => setConfigForm(prev => ({ ...prev, system_prompt: e.target.value }))}
              placeholder="Instrucciones base para LIA..."
            />
            <p className="text-xs text-muted-foreground">
              El contexto del usuario (perfil, diagnóstico) y los documentos RAG se agregan automáticamente.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Modelo</Label>
              <Select
                value={activeConfig.model}
                onValueChange={(v) => setConfigForm(prev => ({ ...prev, model: v }))}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Temperatura: {activeConfig.temperature}</Label>
              <Slider
                className="mt-3"
                min={0}
                max={1}
                step={0.1}
                value={[Number(activeConfig.temperature)]}
                onValueChange={([v]) => setConfigForm(prev => ({ ...prev, temperature: v }))}
              />
              <p className="text-xs text-muted-foreground">0 = preciso, 1 = creativo</p>
            </div>

            <div className="space-y-2">
              <Label>Max tokens</Label>
              <Input
                type="number"
                className="mt-1"
                value={activeConfig.max_tokens}
                onChange={(e) => setConfigForm(prev => ({ ...prev, max_tokens: parseInt(e.target.value) || 2000 }))}
              />
            </div>
          </div>



          <div className="space-y-2 pt-2 border-t border-border/50">
            <Label>Umbral mínimo de relevancia RAG: {Number(activeConfig.min_rrf_score ?? 0).toFixed(3)}</Label>
            <Slider
              className="mt-3"
              min={0}
              max={0.05}
              step={0.001}
              value={[Number(activeConfig.min_rrf_score ?? 0)]}
              onValueChange={([v]) => setConfigForm(prev => ({ ...prev, min_rrf_score: v }))}
            />
            <p className="text-xs text-muted-foreground">
              Si el mejor fragmento recuperado tiene score RRF por debajo de este umbral, LIA responde con conocimiento general en lugar de citar documentos irrelevantes. 0 = sin filtro. Sugerido: 0.010–0.020.
            </p>
          </div>


          {configForm && (
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setConfigForm(null)}>Cancelar</Button>
              <Button onClick={() => {
                if (configForm.system_prompt !== undefined && !configForm.system_prompt?.trim()) {
                  toast({ title: 'El prompt no puede estar vacío', variant: 'destructive' });
                  return;
                }
                updateConfig.mutate(configForm);
              }} disabled={updateConfig.isPending}>
                {updateConfig.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-1" />
                Guardar configuración
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* RAG Documents - File Upload */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-primary" />
              Base de Conocimiento (RAG)
            </CardTitle>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,.md"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Subir Archivo
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Sube documentos (PDF, TXT, MD) que LIA usará como contexto para responder preguntas. Los archivos se procesan en fragmentos con búsqueda semántica.
          </p>
        </CardHeader>
        <CardContent>
          {ragLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : ragDocs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No hay documentos de conocimiento cargados.</p>
          ) : (
            <div className="space-y-2">
              {ragDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-muted/30">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(doc.created_at), "d MMM yyyy", { locale: es })}
                        {doc.file_url && ' • Archivo'}
                        {!doc.file_url && doc.content && ' • Texto manual'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteDocMutation.mutate(doc)}
                    disabled={deleteDocMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
